import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemePreferences } from './themeCatalog';
import { DEFAULT_THEME_PREFERENCES } from './themeCatalog';
import { applyThemePreferences } from './themeManager';
import { bootstrapThemeRegistry } from './themeRegistry';
import { isThemeSyncMessage, isThemeSyncRequest, requestThemeFromParent, themeSyncMessage } from './themeSync';
import {
  canToggleMode,
  migrateThemePreferences,
  nextPreferencesAfterModeToggle,
  preferencesEqual
} from './themeResolve';

export interface ThemeStoreOptions {
  storageKey: string;
}

export interface ThemeStoreState extends ThemePreferences {
  commitPreferences: (prefs: ThemePreferences) => void;
  toggleMode: () => void;
  canToggleMode: () => boolean;
  getPreferences: () => ThemePreferences;
}

function pickPreferences(state: ThemeStoreState): ThemePreferences {
  return {
    family: state.family,
    materialAccent: state.materialAccent,
    genericPalette: state.genericPalette,
    genericCompact: state.genericCompact,
    fluentVariant: state.fluentVariant,
    fluentCompact: state.fluentCompact,
    mode: state.mode
  };
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readPersistedPreferences(storageKey: string): ThemePreferences | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: unknown };
    if (!parsed?.state) return null;
    return migrateThemePreferences(parsed.state);
  } catch {
    return null;
  }
}

export function createThemeStore(options: ThemeStoreOptions) {
  const applyConfiguredTheme = (prefs: ThemePreferences) => applyThemePreferences(prefs);
  let themeSyncBound = false;

  const useThemeStore = create<ThemeStoreState>()(
    persist(
      (set, get) => ({
        ...DEFAULT_THEME_PREFERENCES,
        commitPreferences: (prefs) => {
          applyConfiguredTheme(prefs);
          set(prefs);
        },
        toggleMode: () => {
          const current = pickPreferences(get());
          if (!canToggleMode(current)) return;
          const next = nextPreferencesAfterModeToggle(current);
          applyConfiguredTheme(next);
          set(next);
        },
        canToggleMode: () => canToggleMode(pickPreferences(get())),
        getPreferences: () => pickPreferences(get())
      }),
      {
        name: options.storageKey,
        partialize: (state) => ({
          family: state.family,
          materialAccent: state.materialAccent,
          genericPalette: state.genericPalette,
          genericCompact: state.genericCompact,
          fluentVariant: state.fluentVariant,
          fluentCompact: state.fluentCompact,
          mode: state.mode
        }),
        merge: (persisted, current) => ({
          ...current,
          ...migrateThemePreferences(persisted ?? {})
        }),
        onRehydrateStorage: () => (state) => {
          if (state) applyConfiguredTheme(pickPreferences(state));
        }
      }
    )
  );

  function bootstrapTheme(): void {
    bootstrapThemeRegistry();
    subscribeThemeSync();
    const persisted = readPersistedPreferences(options.storageKey);
    const prefs =
      persisted ??
      migrateThemePreferences({
        mode: systemPrefersDark() ? 'dark' : 'light'
      });
    applyConfiguredTheme(prefs);
    useThemeStore.setState(prefs);
    requestThemeFromParent();
  }

  function subscribeThemeSync(): void {
    if (typeof window === 'undefined' || themeSyncBound) return;
    themeSyncBound = true;

    const applyExternal = (next: ThemePreferences) => {
      const migrated = migrateThemePreferences(next);
      if (preferencesEqual(pickPreferences(useThemeStore.getState()), migrated)) {
        applyConfiguredTheme(migrated);
        return;
      }
      applyConfiguredTheme(migrated);
      useThemeStore.setState(migrated);
    };

    window.addEventListener('storage', (event) => {
      if (event.key !== options.storageKey || !event.newValue) return;
      const persisted = readPersistedPreferences(options.storageKey);
      if (persisted) applyExternal(persisted);
    });

    window.addEventListener('message', (event) => {
      if (event.origin !== window.location.origin) return;
      if (isThemeSyncRequest(event.data)) {
        const source = event.source as Window | null;
        source?.postMessage(themeSyncMessage(pickPreferences(useThemeStore.getState())), event.origin);
        return;
      }
      if (!isThemeSyncMessage(event.data)) return;
      applyExternal(event.data.preferences);
    });
  }

  return {
    useThemeStore,
    bootstrapTheme,
    applyThemePreferences: applyConfiguredTheme,
    preferencesEqual
  };
}

export { preferencesEqual };
